import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { useLocation } from "react-router-dom";
import moment from "moment";
import { notification } from "antd";
import ColumnSearch from "../../common/components/ColumnSearch";
import TableActionButton from "../../common/components/TableActionButton";
import { QueryList, resetFilters } from "../../common/constants/Helpers";
import { LookupRequest } from "../../models/lookup-request";
import { civilRequest } from "../../state/connect-api";
import { useCivilCaseLookupMutation } from "../../state/globalApi";
import { setActiveCaseNumber } from "../../state/globalStore";

interface StateType {
  state: {
    fromPage?: string;
  };
  key: string;
  pathname: string;
  search: string;
  hash: string;
}

const useCivilLookup = () => {
  const [condition, setCondition] = useState(null);
  const [search, setSearch] = useState(null);
  const [returnStatus, setReturnStatus] = useState<boolean>(false);
  const [data, setData] = useState([]);
  const [visibility, setVisibility] = useState<boolean>(false);
  const history = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [civilLookupCase, { isLoading }] = useCivilCaseLookupMutation();

  const handleSearch = async () => {
    if (!condition || !search) {
      notification.error({ message: "Select a search condition and provide a value to search for." });
      return;
    }
    const lookupRequest: LookupRequest = civilRequest(condition, search, "01-01-1753", moment(new Date()).format("MM-DD-YYYY"));
    const results = await civilLookupCase(lookupRequest).unwrap();
    if (condition === "CASENUMBER") {
      await handleSelectCase(results?.LIST[0]?.CasePK);
      localStorage.setItem("caseNumber", search);
    } else {
      setData(results?.LIST);
    }
    localStorage.setItem("condition", condition.toString());
    localStorage.setItem("search", search.toString());
  };

  const handleSelectCase = async (val, viewButton = false) => {
    dispatch(setActiveCaseNumber(viewButton ? val.CasePK : val));
    history("/civil/civil-detail");
    return;
  };

  const columns = [
    { key: "casenumber", dataIndex: "CaseNumber", title: "Case Number", ...ColumnSearch("CaseNumber", "Case", resetFilters) },
    { key: "plantiff", dataIndex: "PrimaryPlaintiffName", title: "Plantiff", ...ColumnSearch("PrimaryPlaintiffName", "Plantiff", resetFilters) },
    { key: "defendant", dataIndex: "PrimaryDefendantName", title: "Defendant", ...ColumnSearch("PrimaryDefendantName", "Defendant", resetFilters) },
    { key: "status", dataIndex: "CaseStatusDesc", title: "Status", ...ColumnSearch("CaseStatusDesc", "Status", resetFilters) },
    {
      key: "action",
      title: "View",
      render: (val) => <TableActionButton onClick={() => handleSelectCase(val, true)} />
    }
  ];

  const handleReturnToResult = async () => {
    const stateObject: StateType = { ...location };
    if (stateObject?.state?.fromPage) {
      let route;
      switch (Number(stateObject.state.fromPage)) {
        case QueryList.CivilDaily:
          route = "/civil-query/daily-query/list";
          break;
        case QueryList.CivilAttorney:
          route = "/civil-query/attorney-query/list";
          break;
      }
      localStorage.clear();
      return history(route, { replace: true, state: stateObject.state.fromPage });
    }
    const conditionValue = localStorage.getItem("condition");
    const searchValue = localStorage.getItem("search");
    setReturnStatus(true);
    const lookupRequest: LookupRequest = civilRequest(conditionValue, searchValue, "01-01-1753", moment(new Date()).format("MM-DD-YYYY"));
    const results = await civilLookupCase(lookupRequest).unwrap();
    setData(results?.LIST);
    history("/civil", { replace: true, state: { returnData: results?.LIST, condition: conditionValue, search: searchValue, returnStatus: true } });
  };

  const handleNewQuery = async () => {
    localStorage.removeItem("condition");
    localStorage.removeItem("search");
    history("/civil");
  };

  const fixedColumns = [
    { key: "field", dataIndex: "FIELD", title: "Field" },
    { key: "description", dataIndex: "DESCRIPTION", title: "Description" }
  ];

  const fixedData = [
    { FIELD: "Plaintiff", DESCRIPTION: "Last name or business name of the Plaintiff only." },
    { FIELD: "Defendant", DESCRIPTION: "Last name or business name of the Defendant only." },
    { FIELD: "Case", DESCRIPTION: "Case Number." }
  ];

  const handleViewPDF = () => {
    setVisibility(true);
  };

  return {
    data,
    setData,
    condition,
    setCondition,
    search,
    loading: isLoading,
    fixedColumns,
    fixedData,
    setSearch,
    handleSearch,
    columns,
    handleReturnToResult,
    handleNewQuery,
    returnStatus,
    setReturnStatus,
    handleViewPDF,
    visibility,
    setVisibility
  };
};

export default useCivilLookup;
